import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDistanceMeters, parseDistanceText, sanitizeDistanceText } from 'src/app/classes/shared/decimal-format.tool';
import { isDuplicateTemplateName } from 'src/app/classes/shared/tracker-config-display.util';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { ButtonComponent, CustomInput, CustomInputComponent, CustomInputEditorComponent } from 'ui-library';

// Librairies
// Services
// Signals
// Variables
export const MAX_KYD_DISTANCE = 30;
export const MIN_KYD_DISTANCE = 0;
const KYD_DISTANCE_FIELDS = ['minDistance', 'maxDistance', 'unfriendDistance', 'exclusionDistance'] as const;

// Exported so resources.component.ts can build a KYD TrackerConfig draft (personal, per-device)
// from these same defaults when a tracker has never been configured by the mobile app yet.
export const defaultSettings = {
  // LED/buzzer/alertProfile deliberately left out - unused, and the KYD form above has no
  // fields for them anyway (see ble-detail.page.ts's own "Alarm settings" section, commented
  // out on the mobile side for the same reason). Leaving them out of a brand new template's
  // settings means they never show up anywhere (create form, detail view, "vue globale" card)
  // instead of surfacing as unlabelled leftovers in the generic settings list.
  kyd: {
    userMode: 'advanced',
    minDistance: 5,
    maxDistance: 15,
    friendGroup: 1,
    ignoreGroup: 0,
    unfriendDistance: 20,
    exclusionDistance: 1,
  },
  sigfox: {
    payloadConfig: '',
    bodyTemplate: '{\r\n' +
      '  "trackerName": "{device}",\r\n' +
      '  "createdDate": "{time}000",\r\n' +
      '  "message": {\r\n' +
      '    "data": "{data}",\r\n' +
      '  \t"seqNumber": "{seqNumber}",\r\n' +
      '  \t"deviceTypeId":"{deviceTypeId}"\r\n' +
      '  }\r\n' +
      '}'
  }
}

@Component({
  selector: 'app-tracker-config-form',
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatFormFieldModule,
    MatSlideToggleModule,
    ButtonComponent,
    CustomInputComponent,
    CustomInputEditorComponent
  ],
  standalone: true,
  templateUrl: './tracker-config-form.component.html',
  styleUrls: ['./tracker-config-form.component.scss']
})

// Classes
export class TrackerConfigFormComponent implements OnChanges {
  // Services
  private readonly translateService: TranslateService = inject(TranslateService);

  // Variables
  @Input() config: TrackerConfig = { id: '', name: '', settings: {} };
  // Set when this form edits a real device's own settings (see resources.component.ts's KYD
  // flow) rather than a standalone reusable TrackerConfig - locks the type select so it can't
  // be switched away from the shape the caller's flat<->nested mapping actually understands.
  @Input() lockType?: string;
  // Hides matching options from the type select - used by the global config list (Devices >
  // gear icon) to keep KYD out of it: KYD configs are per-device/personal now (see
  // resources.component.ts, kyd.component.ts), creating a shared one here would contradict that.
  @Input() excludeTypes: string[] = [];
  // Hides the Name field and skips its required-name/duplicate-name validation in onSave() -
  // used when this form edits a device's own settings directly (Ressources > Dispositif's/the
  // KYD tab's KYD flow) rather than a standalone, named, reusable TrackerConfig: there is
  // nothing to name, saving just deposits the values on the tracker itself. Naming (and its own
  // duplicate check) happens one step later there, via TemplateSaveDialogComponent instead.
  @Input() hideName: boolean = false;
  // Every OTHER existing template (all types) - only used for the duplicate-name check in
  // onSave() below, itself only reachable when hideName is false (tracker-configs.component.ts's
  // global manager, the only caller that doesn't hide the name).
  @Input() existingConfigs: TrackerConfig[] = [];
  // Separate from lockType on purpose: a device-bound form embedded inline (Ressources >
  // Dispositif, no separate "view" state to cancel back to) hides Cancel entirely, but the
  // same lockType usage inside a dialog (Rapport KYD's edit button) still wants Cancel to
  // close it without saving.
  @Input() hideCancel: boolean = false;
  // Also hides "Enregistrer comme modèle" even when hideName is set - used when this form edits
  // the settings of existing TrackerConfig templates directly (tracker-configs.component.ts's
  // "même modification pour tous" bulk edit), where "save as template" would be meaningless:
  // these already ARE templates, each keeping its own name untouched.
  @Input() hideSaveAsTemplate: boolean = false;
  // Hides the form's own "Enregistrer" button too - used when the caller already has its own
  // single save action covering this form plus other fields outside it (edit-tracker-modal.
  // component.ts's sticky footer), so there is only ever one "Enregistrer" button on screen
  // instead of two doing the same thing.
  @Input() hideSave: boolean = false;
  @Output() save = new EventEmitter<void>();
  @Output() cancelForm = new EventEmitter<void>();
  // Only emitted when hideName is set - the standalone "create/edit a named TrackerConfig"
  // usage (tracker-configs.component.ts's global manager, Devices > engrenage) already names
  // things via the form's own Name field and has no separate "save as template" concept to offer.
  @Output() saveAsTemplate = new EventEmitter<void>();

  newConfig: CustomInput | undefined;
  bodyTemplateError: string = "";
  distanceError: string = "";
  nameError: string = "";

  // Text actually shown/edited in the distance fields - kept separate from
  // config.settings[field] (which stays the source of truth, in meters) so a field can hold
  // free-form text like "1," while the user is still typing a decimal, and so it can display
  // the separator matching the site's own language (see decimal-format.tool.ts) instead of
  // whatever `type="number"` would otherwise impose based on the browser's own locale.
  minDistanceText: string = '';
  maxDistanceText: string = '';
  unfriendDistanceText: string = '';
  exclusionDistanceText: string = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config']) {
      this.initDistanceTexts();
      this.initBodyTemplate();
    }
  }

  private initBodyTemplate() {
    const bodyTemplate = this.config.settings?.bodyTemplate;
    if (bodyTemplate != null && typeof bodyTemplate === 'object')
      this.config.settings.bodyTemplate = JSON.stringify(bodyTemplate, null, 4);

    this.bodyTemplateError = "";
  }

  private initDistanceTexts() {
    const lang = this.translateService.currentLang;
    this.minDistanceText = formatDistanceMeters(this.config.settings?.minDistance, lang);
    this.maxDistanceText = formatDistanceMeters(this.config.settings?.maxDistance, lang);
    this.unfriendDistanceText = formatDistanceMeters(this.config.settings?.unfriendDistance, lang);
    this.exclusionDistanceText = formatDistanceMeters(this.config.settings?.exclusionDistance, lang);
  }

  onTypeChanged() {
    if (this.config.type == "KYD") {
      this.config.settings = { ...defaultSettings.kyd };
    }
    if (this.config.type == "Sigfox") {
      this.config.settings = { ...defaultSettings.sigfox };
    }
    this.initDistanceTexts();
  }

  onMinDistanceTextChange(raw: string) { this.minDistanceText = sanitizeDistanceText(raw, this.translateService.currentLang); }
  onMaxDistanceTextChange(raw: string) { this.maxDistanceText = sanitizeDistanceText(raw, this.translateService.currentLang); }
  onUnfriendDistanceTextChange(raw: string) { this.unfriendDistanceText = sanitizeDistanceText(raw, this.translateService.currentLang); }
  onExclusionDistanceTextChange(raw: string) { this.exclusionDistanceText = sanitizeDistanceText(raw, this.translateService.currentLang); }

  onMinDistanceTextBlur() {
    this.onDistanceChange('minDistance', parseDistanceText(this.minDistanceText));
    this.minDistanceText = formatDistanceMeters(this.config.settings.minDistance, this.translateService.currentLang);
  }
  onMaxDistanceTextBlur() {
    this.onDistanceChange('maxDistance', parseDistanceText(this.maxDistanceText));
    this.maxDistanceText = formatDistanceMeters(this.config.settings.maxDistance, this.translateService.currentLang);
  }
  onUnfriendDistanceTextBlur() {
    this.onAlertDistanceChange(parseDistanceText(this.unfriendDistanceText));
    this.unfriendDistanceText = formatDistanceMeters(this.config.settings.unfriendDistance, this.translateService.currentLang);
  }
  onExclusionDistanceTextBlur() {
    this.onDistanceChange('exclusionDistance', parseDistanceText(this.exclusionDistanceText));
    this.exclusionDistanceText = formatDistanceMeters(this.config.settings.exclusionDistance, this.translateService.currentLang);
  }

  // KYD distances are entered in meters and must stay within the device's real detection
  // range (0 to MAX_KYD_DISTANCE - negative makes no physical sense either) - anything outside
  // that is meaningless for the hardware and rejected outright.
  onDistanceChange(field: typeof KYD_DISTANCE_FIELDS[number], value: number | null) {
    if (value != null && (value > MAX_KYD_DISTANCE || value < MIN_KYD_DISTANCE)) {
      this.distanceError = this.translateService.instant('units.device.distance_max_error', { max: MAX_KYD_DISTANCE });
      this.config.settings[field] = null;
    } else {
      this.distanceError = "";
      this.config.settings[field] = value;
    }
  }

  // Defaults legacy configs saved before this mode existed to 'advanced', matching what they
  // already show today (all fields visible) - only an explicit choice from here on switches
  // a config to 'easy'.
  get kydMode(): string {
    return this.config.settings?.userMode ?? 'advanced';
  }

  set kydMode(value: string) {
    if (!this.config.settings) this.config.settings = {};
    this.config.settings.userMode = value;
  }

  // Mirrors the mobile app's Simple mode (see ble-detail.page.ts, unifield-mobile-2): the
  // single "alert distance" field only ever drives unfriendDistance (distance de
  // désappareillage) - minDistance/maxDistance and friendGroup/ignoreGroup are untouched and
  // stay whatever they already were (typically from a previous Advanced-mode save), completely
  // independent of this field.
  onAlertDistanceChange(value: number | null) {
    this.onDistanceChange('unfriendDistance', value);
  }

  // Exclusion mode reuses the existing friendGroup field (already used for the "groupe ami"/
  // "groupe ignoré" pair): 99 = enabled, whatever it was before (or 1) = disabled - same
  // convention as unifield-mobile-2's own ble-detail.page.ts exclusionModeEnabled.
  private lastFriendGroupBeforeExclusion = 1;

  get exclusionModeEnabled(): boolean {
    return this.config.settings?.friendGroup === 99;
  }

  set exclusionModeEnabled(value: boolean) {
    if (!this.config.settings) this.config.settings = {};

    if (value) {
      if (this.config.settings.friendGroup !== 99) this.lastFriendGroupBeforeExclusion = this.config.settings.friendGroup ?? 1;
      this.config.settings.friendGroup = 99;
    } else {
      this.config.settings.friendGroup = this.lastFriendGroupBeforeExclusion;
    }
  }

  hasInvalidDistance(): boolean {
    if (this.config.type !== "KYD" || !this.config.settings) return false;

    return KYD_DISTANCE_FIELDS.some(field => {
      const value = this.config.settings[field];
      return value != null && (value > MAX_KYD_DISTANCE || value < MIN_KYD_DISTANCE);
    });
  }

  onSave() {
    // The backend requires a unique, non-empty name (see TrackerConfigSchema in
    // unifield-api-v3) but only enforces it on creation, not on update - checking it here
    // covers both cases with a clear message instead of a raw 400/"validation process" error.
    // Not applicable at all when hideName is set - there's no name, and nothing gets saved as
    // a TrackerConfig in the first place.
    if (!this.hideName && !this.config.name?.trim()) {
      this.nameError = this.translateService.instant('units.device.config_name_required');
      return;
    }

    // Same check as every "Enregistrer comme modèle" flow's own TemplateSaveDialogComponent
    // step (see isDuplicateTemplateName()) - this form is the one other place a template gets
    // named, so it needs the same friendly pre-check instead of a raw duplicate-key error.
    if (!this.hideName && isDuplicateTemplateName(this.existingConfigs, this.config.name, this.config.id)) {
      this.nameError = this.translateService.instant('trackers.template_name_already_exists');
      return;
    }

    this.nameError = "";

    if (this.hasInvalidDistance()) {
      this.distanceError = this.translateService.instant('units.device.distance_max_error', { max: MAX_KYD_DISTANCE });
      return;
    }

    this.save.emit();
  }

  // No name check here - naming (if needed at all) happens one step later, in the dialog the
  // caller opens in response to this event (see resources.component.ts/kyd-config-dialog.
  // component.ts's saveDraftAsTemplate()/saveAsTemplate()).
  onSaveAsTemplate() {
    if (this.hasInvalidDistance()) {
      this.distanceError = this.translateService.instant('units.device.distance_max_error', { max: MAX_KYD_DISTANCE });
      return;
    }

    this.saveAsTemplate.emit();
  }

  checkBodyTemplate() {
    try {
      let data = JSON.parse(this.config.settings.bodyTemplate);
      if (data)
        this.config.settings.bodyTemplate = JSON.stringify(data, null, 4);

      this.bodyTemplateError = "";
    } catch (e: any) {
      this.bodyTemplateError = e.toString();
    }
  }

  addField() {
    this.newConfig = {
      label: "new",
      name: "new",
      type: "text",
    }
  }

  saveField() {
    if (this.newConfig) {
      if (!this.config.settings)
        this.config.settings = {};

      this.config.settings[this.newConfig.name] = {
        ...this.newConfig,
        value: this.newConfig.value || this.newConfig.default || ""
      };
    }

    this.newConfig = undefined;
  }

  removeField(label: string) {
    const key = Object.keys(this.config.settings).find(k => {
      const setting = this.config.settings[k];
      return setting != null && typeof setting === 'object' && setting.label === label;
    });

    if (key) {
      delete this.config.settings[key];
    }
  }

  getCustomFields(): CustomInput[] {
    if (!this.config.settings) return [];

    // `typeof null === 'object'` in JS - without the null check, a cleared KYD distance field
    // (see onDistanceChange) makes this throw on `.label`, breaking change detection for the
    // whole form (missing Material styling, stuck error messages) on every subsequent render.
    return Object.entries(this.config.settings)
      .filter(([key, value]) => value != null && typeof value === 'object' && (value as any).label)
      .map(([key, value]) => value as CustomInput);
  }
}
